import { useCallback, useEffect, useState } from 'react';
import {
  SearchButtonHoverWrap,
  SearchButtonTooltip,
  Select,
  StateCityNeightbornContent,
} from './styles';
import { SearchAgendasIcon } from '../../../assets/icons/SearchAgendasIcon';
import { NeighborhoodSearchInput } from './components';
import { client as api, ibge as ibgeApi } from '../../../services/api';
import { fetchNeighborhoodSuggestions } from '../../../services/mapsNeighborhoods';

interface StateCityNeightbornProps {
  handlePostalCodeChange: (value: string) => void;
  onSetShouldFetchSchedules: (value: boolean) => void;
}

type SelectedNeighborhood = { id: string; label: string };

function formatPostalCode(found: string): string {
  return found.length === 5 ? `${found}-000` : found.replace(/^(\d{5})(\d{3})$/, '$1-$2');
}

function searchButtonDisabledTooltip(
  hasState: boolean,
  hasCity: boolean,
  hasNeighborhoodPick: boolean
): string {
  if (!hasState) {
    return 'Selecione um estado e uma cidade. Em seguida, escolha um bairro na lista para habilitar Buscar agendas.';
  }
  if (!hasCity) {
    return 'Selecione uma cidade. Em seguida, escolha um bairro na lista para habilitar Buscar agendas.';
  }
  if (!hasNeighborhoodPick) {
    return 'Selecione um bairro na lista de sugestões para habilitar Buscar agendas.';
  }
  return '';
}

const StateCityNeightborn = ({
  handlePostalCodeChange,
  onSetShouldFetchSchedules,
}: StateCityNeightbornProps) => {
  const [states, setStates] = useState<{ code: string; name: string }[]>([]);
  const [cities, setCities] = useState<{ code: number; name: string }[]>([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<SelectedNeighborhood | null>(
    null
  );
  const [error, setError] = useState<boolean>(false);

  const cityName =
    selectedCity && cities.length > 0
      ? cities.find((city) => city.code === parseInt(selectedCity, 10))?.name
      : undefined;

  useEffect(() => {
    const fetchStates = async () => {
      const response = await ibgeApi.get('/localidades/estados');
      const data = response.data;
      const formattedStates = data
        .sort((a: { nome: string }, b: { nome: string }) => a.nome.localeCompare(b.nome))
        .map((state: { sigla: string; nome: string }) => ({
          code: state.sigla,
          name: state.nome,
        }));
      setStates(formattedStates);
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (selectedState) {
      setSelectedCity('');
      setSelectedNeighborhood(null);
      const fetchCities = async () => {
        const response = await ibgeApi.get(`/localidades/estados/${selectedState}/municipios`);
        const data = response.data;
        const formattedCities = data.map((city: { nome: string; id: number }) => ({
          name: city.nome,
          code: city.id,
        }));
        setCities(formattedCities);
      };
      fetchCities();
    } else {
      setCities([]);
      setSelectedCity('');
      setSelectedNeighborhood(null);
    }
  }, [selectedState]);

  useEffect(() => {
    setSelectedNeighborhood(null);
  }, [selectedCity]);

  const loadNeighborhoodSuggestions = useCallback(
    async (searchTerm: string) => {
      if (!cityName) return [];
      try {
        return await fetchNeighborhoodSuggestions(api, cityName, searchTerm);
      } catch (err) {
        console.error('Erro ao buscar bairros:', err);
        return [];
      }
    },
    [cityName]
  );

  const fetchCEP = async (): Promise<string> => {
    if (!cityName || !selectedNeighborhood?.label) return '';

    const address = `${selectedNeighborhood.label}, ${cityName}, ${selectedState}, Brazil`;

    try {
      const geocodeResponse = await api.get(
        `/maps/geocode/json?address=${encodeURIComponent(address)}`
      );
      const geoCodeData = geocodeResponse.data;

      if (geoCodeData.status === 'OK' && geoCodeData.results.length > 0) {
        const { lat, lng } = geoCodeData.results[0].geometry.location;
        const reverseGeocodeResponse = await api.get(`/maps/geocode/json?latlng=${lat},${lng}`);
        const reverseGeocodeData = reverseGeocodeResponse.data;

        if (reverseGeocodeData.status === 'OK' && reverseGeocodeData.results.length > 0) {
          for (const result of reverseGeocodeData.results) {
            const addressComponents = result.address_components;
            const cepComponent = addressComponents.find((component: { types: string[] }) =>
              component.types.includes('postal_code')
            );

            if (cepComponent) {
              return formatPostalCode(cepComponent.long_name as string);
            }
          }
        }
      }
      return '';
    } catch (err) {
      console.error('Erro ao obter as coordenadas geográficas.', err);
      return '';
    }
  };

  const handleSearch = async () => {
    if (
      !selectedState ||
      !selectedCity ||
      !selectedNeighborhood?.id ||
      !selectedNeighborhood.label
    ) {
      setError(true);
      return;
    }

    setError(false);
    const updatedCep = await fetchCEP();
    if (updatedCep) {
      handlePostalCodeChange(updatedCep);
      onSetShouldFetchSchedules(true);
    }
  };

  const hasNeighborhoodPickFromList = Boolean(
    selectedNeighborhood?.id && selectedNeighborhood.label
  );

  const isSearchButtonDisabled = !selectedState || !selectedCity || !hasNeighborhoodPickFromList;

  return (
    <StateCityNeightbornContent onSubmit={(e) => e.preventDefault()}>
      <div className="form-group">
        <label htmlFor="UF">Estado</label>
        <Select
          $isError={error && !selectedState}
          id="UF"
          value={selectedState}
          onChange={(e) => setSelectedState(e.target.value)}
        >
          <option value="">Selecione o estado</option>
          {states.map((state) => (
            <option key={state.code} value={state.code}>
              {state.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="form-group">
        <div className="label-inline-row mb-2 flex items-center gap-1.5">
          <label htmlFor="city" className="m-0 block cursor-default leading-none">
            Cidade
          </label>
          {!selectedState && (
            <span className="group/city-help relative inline-flex shrink-0 items-center">
              <button
                type="button"
                className="inline-flex h-[14px] w-[14px] shrink-0 cursor-help items-center justify-center rounded-full border border-[color:var(--gray-300)] bg-transparent p-0 text-[9px] font-semibold italic leading-none text-[color:var(--gray-500)] hover:border-[color:var(--gray-400)] hover:text-[color:var(--gray-700)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ins-blue)]"
                aria-label="Ajuda sobre o campo Cidade"
                aria-describedby="city-field-help"
              >
                i
              </button>
              <span
                id="city-field-help"
                role="tooltip"
                className="pointer-events-none invisible absolute bottom-[calc(100%+8px)] left-1/2 z-30 w-[min(280px,calc(100vw-48px))] -translate-x-1/2 rounded-md bg-[color:var(--gray-900)] px-3 py-2.5 text-left text-xs font-normal leading-snug text-white shadow-md opacity-0 transition-[opacity,visibility] duration-150 group-hover/city-help:visible group-hover/city-help:opacity-100 group-hover/city-help:pointer-events-auto group-focus-within/city-help:visible group-focus-within/city-help:opacity-100 group-focus-within/city-help:pointer-events-auto"
              >
                Selecione um estado para habilitar o campo Cidade.
                <span
                  className="absolute left-1/2 top-full -ml-1.5 border-[6px] border-transparent border-t-[color:var(--gray-900)]"
                  aria-hidden
                />
              </span>
            </span>
          )}
        </div>
        <Select
          $isError={error && !selectedCity}
          id="city"
          value={selectedCity}
          disabled={!selectedState}
          onChange={(e) => setSelectedCity(e.target.value)}
        >
          <option value="">Selecione a cidade</option>
          {cities.map((city) => (
            <option key={city.code} value={city.code}>
              {city.name}
            </option>
          ))}
        </Select>
      </div>
      <NeighborhoodSearchInput
        key={selectedCity || 'no-city'}
        id="neighborhood"
        cityName={cityName ?? null}
        isError={error && !selectedNeighborhood?.id}
        onSelectionChange={(v) => {
          if (v?.placeId?.trim() && v.displayLabel.trim()) {
            setSelectedNeighborhood({ id: v.placeId, label: v.displayLabel });
          } else {
            setSelectedNeighborhood(null);
          }
        }}
        loadSuggestions={loadNeighborhoodSuggestions}
      />
      <SearchButtonHoverWrap className="search-agendas-wrap">
        <button
          type="button"
          className="search-button"
          disabled={isSearchButtonDisabled}
          onClick={handleSearch}
        >
          <SearchAgendasIcon />
          Buscar agendas
        </button>
        {isSearchButtonDisabled && (
          <SearchButtonTooltip id="search-agendas-btn-help" role="tooltip">
            {searchButtonDisabledTooltip(
              Boolean(selectedState),
              Boolean(selectedCity),
              hasNeighborhoodPickFromList
            )}
          </SearchButtonTooltip>
        )}
      </SearchButtonHoverWrap>
    </StateCityNeightbornContent>
  );
};

export default StateCityNeightborn;
